/* eslint-disable @typescript-eslint/naming-convention */
import { useSafeLayoutEffect } from '@chakra-ui/react';
import { isFunction } from 'lodash';
import { useRef } from 'react';
import useSWR, { Fetcher, Key, Middleware, SWRConfiguration, SWRHook, unstable_serialize } from 'swr';

const normalize = <KeyType = Key, Data = any>(
  args:
    | [KeyType]
    | [KeyType, Fetcher<Data> | null]
    | [KeyType, SWRConfiguration | undefined]
    | [KeyType, Fetcher<Data> | null, SWRConfiguration | undefined]
): [KeyType, Fetcher<Data> | null, Partial<SWRConfiguration<Data>>] => {
  return isFunction(args[1])
    ? [args[0], args[1], args[2] || {}]
    : [args[0], null, (args[1] === null ? args[2] : args[1]) || {}];
};

// Create a custom hook with a middleware
const withMiddleware = (_useSWR: SWRHook, middleware: Middleware): SWRHook => {
  return <Data = any, Error = any>(
    ...args:
      | [Key]
      | [Key, Fetcher<Data> | null]
      | [Key, SWRConfiguration | undefined]
      | [Key, Fetcher<Data> | null, SWRConfiguration | undefined]
  ) => {
    const [key, fn, config] = normalize(args);
    const uses = (config.use || []).concat(middleware);

    return _useSWR<Data, Error>(key, fn, { ...config, use: uses });
  };
};

/**
 * This is an internal implementation of https://github.com/vercel/swr/pull/1263
 * which should be merged some time soon
 * at that point we should remove this implementation and import it from useSwr
 */
export type SWRSubscription<Data = any, Error = any> = (
  key: Key,
  { next }: { next: (err?: Error, data?: Data) => void }
) => void;

export interface SWRSubscriptionResponse<Data = any, Error = any> {
  data?: Data;
  error?: Error;
}

export type SWRSubscriptionHook<Data = any, Error = any> = (
  key: Key,
  subscribe: SWRSubscription<Data, Error>,
  config?: SWRConfiguration
) => SWRSubscriptionResponse<Data, Error>;

const subscriptions = new Map<Key, number>();
const disposers = new Map();

export const subscription = ((<Data, Error>(useSWRNext: SWRHook) => (
  _key: Key,
  subscribe: SWRSubscription<Data, Error>,
  config?: SWRConfiguration
): SWRSubscriptionResponse<Data, Error> => {
  const key = unstable_serialize(_key);

  const swr = useSWRNext(key, null, config);
  const subscribeRef = useRef(subscribe);

  useSafeLayoutEffect(() => {
    subscribeRef.current = subscribe;
  });

  useSafeLayoutEffect(() => {
    if (!key) return;
    subscriptions.set(key, (subscriptions.get(key) || 0) + 1);

    const onData = (val?: Data) => swr.mutate(val, false);
    const onError = async (err: any) => {
      // Avoid thrown errors from `mutate`
      // eslint-disable-next-line no-empty
      try {
        await swr.mutate(() => {
          throw err;
        }, false);
      } catch (_) {
        /* eslint-disable-line no-empty */
      }
    };

    const next = (_err?: any, _data?: Data) => {
      if (_err) onError(_err);
      else onData(_data);
    };

    if (subscriptions.get(key) === 1) {
      const dispose = subscribeRef.current(key, { next });

      disposers.set(key, dispose);
    }
    return () => {
      // Prevent frequent unsubscribe caused by unmount
      setTimeout(() => {
        const count = subscriptions.get(key) || 1;

        subscriptions.set(key, count - 1);
        // Dispose if it's last one
        if (count === 1) {
          disposers.get(key)();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    get data() {
      return swr.data;
    },
    get error() {
      return swr.error;
    },
  };
}) as unknown) as Middleware;

const useSWRSubscription = withMiddleware(useSWR, subscription) as SWRSubscriptionHook;

export default useSWRSubscription;
